import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "@/lib/api";

const ROUTE = {
  order: (id) => `/orders/${id}`,
  customer: (id) => `/masters/customers?open=${id}`,
  part: (id) => `/masters/items`,
  item: (id) => `/masters/items?open=${id}`,
  design: (id) => `/designs/${id}`,
};

export function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "/" && !["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) {
        e.preventDefault();
        ref.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(() => {
      api.get("/search", { params: { q } }).then((r) => setResults(r.data)).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const go = (r) => {
    navigate(ROUTE[r.type](r.id));
    setOpen(false);
    setQ("");
  };

  return (
    <div className="relative w-full max-w-xl">
      <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
      <input ref={ref} value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Search orders, customers (name/GSTIN), parts, designs…  press /"
        className="w-full h-9 pl-9 pr-3 text-sm rounded border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500"
        data-testid="global-search-input" />
      {open && q.trim().length >= 2 && (
        <div className="absolute mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg z-50 max-h-96 overflow-auto" data-testid="global-search-results">
          {results.length === 0 && <div className="px-3 py-3 text-sm text-slate-500">No matches</div>}
          {results.map((r) => (
            <button key={`${r.type}-${r.id}`} onMouseDown={() => go(r)} data-testid={`search-result-${r.type}-${r.id}`}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-3 border-b border-slate-100 last:border-0">
              <span className="text-[10px] uppercase font-mono w-16 shrink-0 text-sky-700">{r.type}</span>
              <span className="text-sm font-medium text-slate-900 truncate">{r.title}</span>
              <span className="text-xs text-slate-500 truncate">{r.subtitle}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
